import React, { useState, createElement, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SendIcon, SettingsIcon, HistoryIcon, CopyIcon, ThumbsUpIcon, ThumbsDownIcon, RotateCcwIcon, XIcon, Trash2Icon, PlusIcon, MoreHorizontalIcon, ShareIcon, DownloadIcon, ChevronLeftIcon, TwitterIcon, FacebookIcon, LinkedinIcon, LinkIcon, UploadIcon, ChefHatIcon, UtensilsIcon, ImageIcon, WandIcon, Sparkles, ZapIcon, CodeIcon, PlayIcon, FileCodeIcon, TerminalIcon, CheckIcon, AlertCircleIcon, CloudIcon, TreesIcon, SparkleIcon, TimerIcon, ScaleIcon, ShoppingCartIcon, BookmarkIcon, ClockIcon, FlameIcon, LeafIcon, GlobeIcon, ArrowLeftRightIcon, VolumeIcon, Volume2Icon, BookOpenIcon, StarIcon, LanguagesIcon } from 'lucide-react';
import { mockApps } from '../utils/mockData';
import { getIcon } from '../utils/iconUtils';
type Message = {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
};
type HistoryItem = {
  id: string;
  prompt: string;
  timestamp: Date;
};
type CookingPreferences = {
  dietaryRestrictions: string[];
  allergies: string[];
  measurementSystem: 'metric' | 'imperial';
  skillLevel: 'beginner' | 'intermediate' | 'advanced';
  availableEquipment: string[];
  cuisinePreferences: string[];
  cookingTime: string;
  servingSize: number;
};
type AppTab = {
  id: string;
  appId: string;
  messages: Message[];
  history: HistoryItem[];
  prompt: string;
  cookingPreferences?: CookingPreferences;
};
type ImageGenerationParams = {
  style: string;
  aspectRatio: string;
  quality: string;
  numImages: number;
};
type GeneratedImage = {
  id: string;
  url: string;
  prompt: string;
  timestamp: Date;
  params: ImageGenerationParams;
};
type CodeAction = 'optimize' | 'explain' | 'debug' | 'convert';
type GhiblifiStyle = 'totoro-forest' | 'spirited-away' | 'howls-castle' | 'ponyo-sea' | 'classic';
type TransformedImage = {
  id: string;
  originalUrl: string;
  transformedUrl: string;
  style: GhiblifiStyle;
  intensity: number;
  timestamp: Date;
};
type Recipe = {
  id: string;
  title: string;
  ingredients: {
    item: string;
    amount: string;
    unit: string;
  }[];
  steps: string[];
  prepTime: number;
  cookTime: number;
  servings: number;
  difficulty: 'easy' | 'medium' | 'hard';
  nutrition?: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
};
type TranslationMode = 'text' | 'document' | 'conversation';
type FormalityLevel = 'casual' | 'neutral' | 'formal' | 'business';
type Language = {
  code: string;
  name: string;
  flag: string;
};
type Translation = {
  id: string;
  sourceText: string;
  translatedText: string;
  sourceLang: string;
  targetLang: string;
  formality: FormalityLevel;
  timestamp: Date;
  isFavorite: boolean;
};
const LANGUAGES: Language[] = [{
  code: 'en',
  name: 'English',
  flag: '🇬🇧'
}, {
  code: 'es',
  name: 'Spanish',
  flag: '🇪🇸'
}, {
  code: 'fr',
  name: 'French',
  flag: '🇫🇷'
}, {
  code: 'de',
  name: 'German',
  flag: '🇩🇪'
}, {
  code: 'it',
  name: 'Italian',
  flag: '🇮🇹'
}, {
  code: 'pt',
  name: 'Portuguese',
  flag: '🇵🇹'
}, {
  code: 'ru',
  name: 'Russian',
  flag: '🇷🇺'
}, {
  code: 'ja',
  name: 'Japanese',
  flag: '🇯🇵'
}, {
  code: 'zh',
  name: 'Chinese',
  flag: '🇨🇳'
}, {
  code: 'ko',
  name: 'Korean',
  flag: '🇰🇷'
}, {
  code: 'ar',
  name: 'Arabic',
  flag: '🇸🇦'
}, {
  code: 'hi',
  name: 'Hindi',
  flag: '🇮🇳'
}];
const COMMON_PHRASES = [{
  category: 'Greetings',
  phrases: ['Hello', 'Good morning', 'How are you?', 'Nice to meet you']
}, {
  category: 'Travel',
  phrases: ['Where is the bathroom?', 'How much does this cost?', 'I need help', 'Thank you']
}, {
  category: 'Business',
  phrases: ['Please review the document', "Let's schedule a meeting", 'I agree with your proposal']
}];
export const AppInterface = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const app = mockApps.find(a => a.id === id);
  // Tab management
  const [tabs, setTabs] = useState<AppTab[]>([{
    id: '1',
    appId: id || '',
    messages: [],
    history: [],
    prompt: '',
    cookingPreferences: {
      dietaryRestrictions: [],
      allergies: [],
      measurementSystem: 'imperial',
      skillLevel: 'intermediate',
      availableEquipment: [],
      cuisinePreferences: [],
      cookingTime: 'any',
      servingSize: 4
    }
  }]);
  const [activeTabId, setActiveTabId] = useState('1');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showTabMenu, setShowTabMenu] = useState(false);
  const [showAppPicker, setShowAppPicker] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareContent, setShareContent] = useState('');
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showCookingPreferences, setShowCookingPreferences] = useState(false);
  // Settings state
  const [settings, setSettings] = useState({
    model: 'gpt-4',
    temperature: 0.7,
    maxTokens: 2000,
    topP: 1
  });
  const activeTab = tabs.find(t => t.id === activeTabId);
  // Image Wizard Pro state
  const [imageParams, setImageParams] = useState<ImageGenerationParams>({
    style: 'realistic',
    aspectRatio: '1:1',
    quality: 'standard',
    numImages: 1
  });
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [selectedGeneratedImage, setSelectedGeneratedImage] = useState<GeneratedImage | null>(null);
  // CodeCraft AI state
  const [inputCode, setInputCode] = useState('');
  const [outputCode, setOutputCode] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('javascript');
  const [codeAction, setCodeAction] = useState<CodeAction>('optimize');
  const [isProcessingCode, setIsProcessingCode] = useState(false);
  const [consoleOutput, setConsoleOutput] = useState<string[]>([]);
  // Ghiblifi state
  const [ghiblifiStyle, setGhiblifiStyle] = useState<GhiblifiStyle>('totoro-forest');
  const [styleIntensity, setStyleIntensity] = useState(70);
  const [transformedImages, setTransformedImages] = useState<TransformedImage[]>([]);
  const [isTransforming, setIsTransforming] = useState(false);
  const [selectedTransformedImage, setSelectedTransformedImage] = useState<TransformedImage | null>(null);
  const [showBeforeAfter, setShowBeforeAfter] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(50);
  // Cooking Helper state
  const [currentRecipe, setCurrentRecipe] = useState<Recipe | null>(null);
  const [servingMultiplier, setServingMultiplier] = useState(1);
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [timers, setTimers] = useState<{
    [key: number]: number;
  }>({});
  const [showNutrition, setShowNutrition] = useState(false);
  const [shoppingList, setShoppingList] = useState<string[]>([]);
  const [isGeneratingRecipe, setIsGeneratingRecipe] = useState(false);
  // Translation App state
  const [translationMode, setTranslationMode] = useState<TranslationMode>('text');
  const [sourceText, setSourceText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [sourceLang, setSourceLang] = useState('en');
  const [targetLang, setTargetLang] = useState('es');
  const [formality, setFormality] = useState<FormalityLevel>('neutral');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationHistory, setTranslationHistory] = useState<Translation[]>([]);
  const [showPhrases, setShowPhrases] = useState(false);
  const [autoDetect, setAutoDetect] = useState(true);
  const [detectedLang, setDetectedLang] = useState<string | null>(null);
  // Dietary restrictions options
  const dietaryOptions = ['Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Keto', 'Paleo', 'Low-Carb', 'Low-Fat', 'Halal', 'Kosher'];
  const equipmentOptions = ['Oven', 'Stovetop', 'Microwave', 'Air Fryer', 'Slow Cooker', 'Instant Pot', 'Blender', 'Food Processor', 'Stand Mixer', 'Grill', 'Cast Iron Skillet', 'Non-Stick Pan', 'Wok', 'Dutch Oven'];
  const cuisineOptions = ['Italian', 'Mexican', 'Chinese', 'Japanese', 'Indian', 'Thai', 'Mediterranean', 'French', 'American', 'Korean'];
  const updateCookingPreferences = (preferences: Partial<CookingPreferences>) => {
    setTabs(tabs.map(t => t.id === activeTabId ? {
      ...t,
      cookingPreferences: {
        ...t.cookingPreferences!,
        ...preferences
      }
    } : t));
  };
  const toggleArrayItem = (array: string[], item: string, field: keyof CookingPreferences) => {
    const newArray = array.includes(item) ? array.filter(i => i !== item) : [...array, item];
    updateCookingPreferences({
      [field]: newArray
    });
  };
  const openAppInNewTab = (appId: string) => {
    const existingTab = tabs.find(t => t.appId === appId);
    if (existingTab) {
      setActiveTabId(existingTab.id);
      setShowAppPicker(false);
      return;
    }
    const newTab: AppTab = {
      id: Date.now().toString(),
      appId: appId,
      messages: [],
      history: [],
      prompt: '',
      cookingPreferences: appId === 'cooking-helper' ? {
        dietaryRestrictions: [],
        allergies: [],
        measurementSystem: 'imperial',
        skillLevel: 'intermediate',
        availableEquipment: [],
        cuisinePreferences: [],
        cookingTime: 'any',
        servingSize: 4
      } : undefined
    };
    setTabs([...tabs, newTab]);
    setActiveTabId(newTab.id);
    setShowAppPicker(false);
    navigate(`/app-interface/${appId}`);
  };
  const closeTab = (tabId: string) => {
    const newTabs = tabs.filter(t => t.id !== tabId);
    if (newTabs.length === 0) {
      navigate('/my-apps');
      return;
    }
    setTabs(newTabs);
    if (activeTabId === tabId) {
      setActiveTabId(newTabs[0].id);
      const newActiveTab = newTabs[0];
      navigate(`/app-interface/${newActiveTab.appId}`);
    }
  };
  const goBackToMyApps = () => {
    navigate('/my-apps');
  };
  const updateTabPrompt = (prompt: string) => {
    setTabs(tabs.map(t => t.id === activeTabId ? {
      ...t,
      prompt
    } : t));
  };
  const buildContextualPrompt = (userPrompt: string): string => {
    if (app?.id !== 'cooking-helper' || !activeTab?.cookingPreferences) {
      return userPrompt;
    }
    const prefs = activeTab.cookingPreferences;
    let contextParts: string[] = [];
    if (prefs.dietaryRestrictions.length > 0) {
      contextParts.push(`Dietary restrictions: ${prefs.dietaryRestrictions.join(', ')}`);
    }
    if (prefs.allergies.length > 0) {
      contextParts.push(`Allergies: ${prefs.allergies.join(', ')}`);
    }
    if (prefs.availableEquipment.length > 0) {
      contextParts.push(`Available equipment: ${prefs.availableEquipment.join(', ')}`);
    }
    if (prefs.cuisinePreferences.length > 0) {
      contextParts.push(`Preferred cuisines: ${prefs.cuisinePreferences.join(', ')}`);
    }
    contextParts.push(`Measurement system: ${prefs.measurementSystem}`, `Skill level: ${prefs.skillLevel}`, `Serving size: ${prefs.servingSize} people`);
    if (prefs.cookingTime !== 'any') {
      contextParts.push(`Time available: ${prefs.cookingTime}`);
    }
    const context = contextParts.join('. ');
    return `${context}.\n\nUser request: ${userPrompt}`;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeTab?.prompt.trim() || isGenerating) return;
    const contextualPrompt = buildContextualPrompt(activeTab.prompt);
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: activeTab.prompt,
      timestamp: new Date()
    };
    const newHistory: HistoryItem = {
      id: Date.now().toString(),
      prompt: activeTab.prompt,
      timestamp: new Date()
    };
    setTabs(tabs.map(t => t.id === activeTabId ? {
      ...t,
      messages: [...t.messages, userMessage],
      history: [newHistory, ...t.history],
      prompt: ''
    } : t));
    setIsGenerating(true);
    setTimeout(() => {
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: 'This is a simulated response. In a real implementation, this would be the actual AI-generated content based on your prompt and cooking preferences.',
        timestamp: new Date()
      };
      setTabs(prevTabs => prevTabs.map(t => t.id === activeTabId ? {
        ...t,
        messages: [...t.messages, assistantMessage]
      } : t));
      setIsGenerating(false);
    }, 2000);
  };
  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content);
  };
  const handleShare = (content: string) => {
    setShareContent(content);
    setShowShareModal(true);
  };
  const handleDownload = (content: string) => {
    const blob = new Blob([content], {
      type: 'text/plain'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `response-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const handleShareTo = (platform: string) => {
    const text = encodeURIComponent(shareContent);
    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?quote=${text}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(shareContent);
        break;
    }
    setShowShareModal(false);
  };
  const handleClearHistory = () => {
    setTabs(tabs.map(t => t.id === activeTabId ? {
      ...t,
      messages: []
    } : t));
  };
  const loadHistoryItem = (item: HistoryItem) => {
    updateTabPrompt(item.prompt);
    setShowHistory(false);
  };
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const removeImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
  };
  // Ghiblifi functions
  const handleGhiblifiTransform = async () => {
    if (!uploadedImage || isTransforming) return;
    setIsTransforming(true);
    setTimeout(() => {
      const transformed: TransformedImage = {
        id: Date.now().toString(),
        originalUrl: imagePreview!,
        transformedUrl: `https://picsum.photos/seed/ghibli-${Date.now()}/800/600`,
        style: ghiblifiStyle,
        intensity: styleIntensity,
        timestamp: new Date()
      };
      setTransformedImages([transformed, ...transformedImages]);
      setSelectedTransformedImage(transformed);
      setShowBeforeAfter(true);
      setIsTransforming(false);
    }, 3000);
  };
  // Cooking Helper functions
  const handleGenerateRecipe = async () => {
    if (!activeTab?.prompt.trim() || isGeneratingRecipe) return;
    setIsGeneratingRecipe(true);
    setTimeout(() => {
      const recipe: Recipe = {
        id: Date.now().toString(),
        title: 'Delicious Pasta Carbonara',
        ingredients: [{
          item: 'Spaghetti',
          amount: '400',
          unit: 'g'
        }, {
          item: 'Eggs',
          amount: '4',
          unit: 'whole'
        }, {
          item: 'Pancetta',
          amount: '200',
          unit: 'g'
        }, {
          item: 'Parmesan cheese',
          amount: '100',
          unit: 'g'
        }, {
          item: 'Black pepper',
          amount: '1',
          unit: 'tsp'
        }, {
          item: 'Salt',
          amount: 'to taste',
          unit: ''
        }],
        steps: ['Bring a large pot of salted water to boil and cook spaghetti according to package directions.', 'While pasta cooks, dice the pancetta and cook in a large skillet over medium heat until crispy, about 5-7 minutes.', 'In a bowl, whisk together eggs, grated Parmesan, and black pepper.', 'Reserve 1 cup of pasta water, then drain the pasta.', 'Remove skillet from heat, add hot pasta to the pancetta, and toss to combine.', 'Slowly pour the egg mixture over the pasta, tossing constantly. Add pasta water as needed to create a creamy sauce.', 'Serve immediately with extra Parmesan and black pepper.'],
        prepTime: 10,
        cookTime: 20,
        servings: 4,
        difficulty: 'medium',
        nutrition: {
          calories: 520,
          protein: 24,
          carbs: 58,
          fat: 20
        }
      };
      setCurrentRecipe(recipe);
      setActiveStep(0);
      setCompletedSteps([]);
      setIsGeneratingRecipe(false);
      updateTabPrompt('');
    }, 2000);
  };
  const toggleStep = (stepIndex: number) => {
    if (completedSteps.includes(stepIndex)) {
      setCompletedSteps(completedSteps.filter(s => s !== stepIndex));
    } else {
      setCompletedSteps([...completedSteps, stepIndex]);
    }
  };
  const adjustServings = (newServings: number) => {
    if (!currentRecipe) return;
    const multiplier = newServings / currentRecipe.servings;
    setServingMultiplier(multiplier);
  };
  const addToShoppingList = () => {
    if (!currentRecipe) return;
    const items = currentRecipe.ingredients.map(ing => `${parseFloat(ing.amount) * servingMultiplier} ${ing.unit} ${ing.item}`);
    setShoppingList([...shoppingList, ...items]);
  };
  // Image Wizard Pro functions
  const handleGenerateImage = async () => {
    if (!activeTab?.prompt.trim() || isGeneratingImage) return;
    setIsGeneratingImage(true);
    // Simulate image generation
    setTimeout(() => {
      const newImages: GeneratedImage[] = [];
      for (let i = 0; i < imageParams.numImages; i++) {
        newImages.push({
          id: `${Date.now()}-${i}`,
          url: `https://picsum.photos/seed/${Date.now() + i}/800/800`,
          prompt: activeTab!.prompt,
          timestamp: new Date(),
          params: {
            ...imageParams
          }
        });
      }
      setGeneratedImages([...newImages, ...generatedImages]);
      setSelectedGeneratedImage(newImages[0]);
      setIsGeneratingImage(false);
      updateTabPrompt('');
    }, 2000);
  };
  const handleDownloadImage = (image: GeneratedImage) => {
    const link = document.createElement('a');
    link.href = image.url;
    link.download = `generated-${image.id}.png`;
    link.click();
  };
  // CodeCraft AI functions
  const handleProcessCode = async () => {
    if (!inputCode.trim() || isProcessingCode) return;
    setIsProcessingCode(true);
    setConsoleOutput([`Processing code with action: ${codeAction}...`]);
    // Simulate code processing
    setTimeout(() => {
      let result = '';
      switch (codeAction) {
        case 'optimize':
          result = `// Optimized ${selectedLanguage} code\n${inputCode}\n\n// Performance improvements applied`;
          setConsoleOutput([...consoleOutput, '✓ Code optimized successfully', '✓ Reduced complexity by 30%', '✓ Improved readability']);
          break;
        case 'explain':
          result = `/* Code Explanation:\n * This code performs...\n * Key concepts: ${selectedLanguage} syntax\n */\n${inputCode}`;
          setConsoleOutput([...consoleOutput, '✓ Code analysis complete', '✓ Added inline comments']);
          break;
        case 'debug':
          result = `// Debugged code\n${inputCode}\n\n// Fixed potential issues`;
          setConsoleOutput([...consoleOutput, '✓ Found 2 potential issues', '✓ Applied fixes']);
          break;
        case 'convert':
          result = `// Converted to ${selectedLanguage}\n${inputCode}`;
          setConsoleOutput([...consoleOutput, '✓ Code converted successfully']);
          break;
      }
      setOutputCode(result);
      setIsProcessingCode(false);
    }, 1500);
  };
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setConsoleOutput([...consoleOutput, '✓ Code copied to clipboard']);
  };
  // Translation functions
  const handleTranslate = async () => {
    if (!sourceText.trim() || isTranslating) return;
    setIsTranslating(true);
    // Simulate translation
    setTimeout(() => {
      const translated = `[Translated from ${LANGUAGES.find(l => l.code === sourceLang)?.name} to ${LANGUAGES.find(l => l.code === targetLang)?.name}]\n\n${sourceText}\n\n(This is a simulated translation. In production, this would use a real translation API.)`;
      setTranslatedText(translated);
      const newTranslation: Translation = {
        id: Date.now().toString(),
        sourceText,
        translatedText: translated,
        sourceLang,
        targetLang,
        formality,
        timestamp: new Date(),
        isFavorite: false
      };
      setTranslationHistory([newTranslation, ...translationHistory]);
      setIsTranslating(false);
    }, 1500);
  };
  const swapLanguages = () => {
    const tempLang = sourceLang;
    const tempText = sourceText;
    setSourceLang(targetLang);
    setTargetLang(tempLang);
    setSourceText(translatedText);
    setTranslatedText(tempText);
  };
  const copyTranslation = () => {
    navigator.clipboard.writeText(translatedText);
    // Show toast notification
  };
  const toggleFavorite = (id: string) => {
    setTranslationHistory(translationHistory.map(t => t.id === id ? {
      ...t,
      isFavorite: !t.isFavorite
    } : t));
  };
  const loadFromHistory = (translation: Translation) => {
    setSourceText(translation.sourceText);
    setTranslatedText(translation.translatedText);
    setSourceLang(translation.sourceLang);
    setTargetLang(translation.targetLang);
    setFormality(translation.formality);
  };
  if (!app) {
    return <div className="flex items-center justify-center h-screen bg-gray-50" data-id="element-668">
        <div className="text-center" data-id="element-669">
          <p className="text-gray-500 mb-4" data-id="element-670">App not found</p>
          <button onClick={() => navigate('/my-apps')} className="text-indigo-600 hover:text-indigo-700 font-medium" data-id="element-671">
            Return to My Apps
          </button>
        </div>
      </div>;
  }
  const getWelcomeContent = () => {
    switch (app.id) {
      case 'cooking-helper':
        return {
          emoji: '👨‍🍳',
          examples: ['What can I make with chicken, rice, and vegetables?', 'Give me a quick 30-minute dinner recipe', 'How do I make the perfect pasta carbonara?']
        };
      case 'ghiblifi':
        return {
          emoji: '🎨',
          examples: ['Transform my garden into a Ghibli scene', 'Create a whimsical forest path in Ghibli style', 'Make a cozy cottage with Ghibli aesthetics']
        };
      case 'eli5-explanations':
        return {
          emoji: '🧠',
          examples: ["Explain quantum computing like I'm five", 'How does the internet work in simple terms?', 'What is cryptocurrency in easy words?']
        };
      default:
        return {
          emoji: '💡',
          examples: ['Create an image of a sunset over mountains', 'Generate a creative story about space travel', 'Help me write a professional email']
        };
    }
  };
  const welcomeContent = getWelcomeContent();
  // Render specialized interface based on app type
  const renderAppInterface = () => {
    // Translation App Interface
    if (app.id === 'lingua-translate') {
      return <div className="flex-1 flex flex-col overflow-hidden bg-gradient-to-br from-blue-50 to-cyan-50" data-id="element-672">
          {/* Top Controls */}
          <div className="border-b border-blue-200/50 bg-white/90 backdrop-blur-sm px-6 py-4" data-id="element-673">
            <div className="flex items-center justify-between max-w-7xl mx-auto" data-id="element-674">
              {/* Mode Selector */}
              <div className="flex items-center space-x-2" data-id="element-675">
                {(['text', 'document', 'conversation'] as TranslationMode[]).map(mode => <button key={mode} onClick={() => setTranslationMode(mode)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize ${translationMode === mode ? 'bg-blue-600 text-white' : 'bg-blue-50 text-gray-700 hover:bg-blue-100'}`} data-id="element-676">
                    {mode}
                  </button>)}
              </div>

              {/* Formality Selector */}
              <div className="flex items-center space-x-3" data-id="element-677">
                <span className="text-sm text-gray-600" data-id="element-678">Tone:</span>
                <select value={formality} onChange={e => setFormality(e.target.value as FormalityLevel)} className="border border-blue-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" data-id="element-679">
                  <option value="casual" data-id="element-680">Casual</option>
                  <option value="neutral" data-id="element-681">Neutral</option>
                  <option value="formal" data-id="element-682">Formal</option>
                  <option value="business" data-id="element-683">Business</option>
                </select>

                <button onClick={() => setShowPhrases(!showPhrases)} className={`p-2 rounded-lg transition-colors ${showPhrases ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`} title="Common Phrases" data-id="element-684">
                  <BookOpenIcon className="h-5 w-5" data-id="element-685" />
                </button>
              </div>
            </div>
          </div>

          {/* Main Translation Area */}
          <div className="flex-1 flex overflow-hidden" data-id="element-686">
            {/* Source Text */}
            <div className="flex-1 flex flex-col border-r border-blue-200/50" data-id="element-687">
              {/* Language Selector */}
              <div className="bg-white/80 backdrop-blur-sm border-b border-blue-200/50 px-6 py-3 flex items-center justify-between" data-id="element-688">
                <div className="flex items-center space-x-3" data-id="element-689">
                  <select value={sourceLang} onChange={e => setSourceLang(e.target.value)} className="border-0 bg-transparent text-lg font-medium focus:outline-none focus:ring-0 cursor-pointer" data-id="element-690">
                    {LANGUAGES.map(lang => <option key={lang.code} value={lang.code} data-id="element-691">
                        {lang.flag} {lang.name}
                      </option>)}
                  </select>
                  {autoDetect && detectedLang && <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded" data-id="element-692">
                      Detected
                    </span>}
                </div>
                <div className="flex items-center space-x-2" data-id="element-693">
                  <label className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer" data-id="element-694">
                    <input type="checkbox" checked={autoDetect} onChange={e => setAutoDetect(e.target.checked)} className="rounded text-blue-600" data-id="element-695" />
                    <span data-id="element-696">Auto-detect</span>
                  </label>
                  <button className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Play pronunciation" data-id="element-697">
                    <Volume2Icon className="h-4 w-4" data-id="element-698" />
                  </button>
                </div>
              </div>

              {/* Text Area */}
              <div className="flex-1 p-6" data-id="element-699">
                <textarea value={sourceText} onChange={e => setSourceText(e.target.value)} placeholder="Enter text to translate..." className="w-full h-full resize-none border-0 focus:outline-none text-lg bg-transparent" spellCheck={false} data-id="element-700" />
              </div>

              {/* Character Count */}
              <div className="bg-white/80 backdrop-blur-sm border-t border-blue-200/50 px-6 py-3 flex items-center justify-between" data-id="element-701">
                <span className="text-sm text-gray-500" data-id="element-702">
                  {sourceText.length} characters
                </span>
                <button onClick={() => setSourceText('')} className="text-sm text-gray-600 hover:text-red-600 transition-colors" data-id="element-703">
                  Clear
                </button>
              </div>
            </div>

            {/* Swap Button */}
            <div className="flex items-center justify-center px-4 bg-gradient-to-b from-blue-50 to-cyan-50" data-id="element-704">
              <button onClick={swapLanguages} className="p-3 bg-white border-2 border-blue-300 rounded-full hover:bg-blue-50 hover:border-blue-400 transition-all shadow-md hover:shadow-lg" title="Swap languages" data-id="element-705">
                <ArrowLeftRightIcon className="h-5 w-5 text-blue-600" data-id="element-706" />
              </button>
            </div>

            {/* Translated Text */}
            <div className="flex-1 flex flex-col" data-id="element-707">
              {/* Language Selector */}
              <div className="bg-white/80 backdrop-blur-sm border-b border-blue-200/50 px-6 py-3 flex items-center justify-between" data-id="element-708">
                <select value={targetLang} onChange={e => setTargetLang(e.target.value)} className="border-0 bg-transparent text-lg font-medium focus:outline-none focus:ring-0 cursor-pointer" data-id="element-709">
                  {LANGUAGES.map(lang => <option key={lang.code} value={lang.code} data-id="element-710">
                      {lang.flag} {lang.name}
                    </option>)}
                </select>
                <button className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Play pronunciation" data-id="element-711">
                  <Volume2Icon className="h-4 w-4" data-id="element-712" />
                </button>
              </div>

              {/* Translation Display */}
              <div className="flex-1 p-6 bg-white/50" data-id="element-713">
                {translatedText ? <div className="text-lg leading-relaxed text-gray-800" data-id="element-714">
                    {translatedText}
                  </div> : <div className="flex items-center justify-center h-full" data-id="element-715">
                    <div className="text-center text-gray-400" data-id="element-716">
                      <LanguagesIcon className="h-12 w-12 mx-auto mb-3 opacity-50" data-id="element-717" />
                      <p className="text-sm" data-id="element-718">Translation will appear here</p>
                    </div>
                  </div>}
              </div>

              {/* Actions */}
              <div className="bg-white/80 backdrop-blur-sm border-t border-blue-200/50 px-6 py-3 flex items-center justify-between" data-id="element-719">
                <div className="flex items-center space-x-2" data-id="element-720">
                  <button onClick={copyTranslation} disabled={!translatedText} className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-721">
                    <CopyIcon className="h-4 w-4" data-id="element-722" />
                    <span data-id="element-723">Copy</span>
                  </button>
                  <button disabled={!translatedText} className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-724">
                    <ShareIcon className="h-4 w-4" data-id="element-725" />
                    <span data-id="element-726">Share</span>
                  </button>
                </div>
                <button onClick={handleTranslate} disabled={!sourceText.trim() || isTranslating} className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-2 rounded-lg font-medium hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg flex items-center space-x-2" data-id="element-727">
                  {isTranslating ? <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" data-id="element-728" />
                      <span data-id="element-729">Translating...</span>
                    </> : <>
                      <LanguagesIcon className="h-4 w-4" data-id="element-730" />
                      <span data-id="element-731">Translate</span>
                    </>}
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Panel - History & Phrases */}
          <div className="border-t border-blue-200/50 bg-white/90 backdrop-blur-sm" data-id="element-732">
            <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-733">
              {showPhrases ? <div data-id="element-734">
                  <div className="flex items-center justify-between mb-3" data-id="element-735">
                    <h3 className="text-sm font-semibold text-gray-700" data-id="element-736">
                      Common Phrases
                    </h3>
                    <button onClick={() => setShowPhrases(false)} className="text-sm text-gray-600 hover:text-gray-800" data-id="element-737">
                      Hide
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-738">
                    {COMMON_PHRASES.map(category => <div key={category.category} data-id="element-739">
                        <h4 className="text-xs font-medium text-gray-500 mb-2" data-id="element-740">
                          {category.category}
                        </h4>
                        <div className="space-y-1" data-id="element-741">
                          {category.phrases.map((phrase, idx) => <button key={idx} onClick={() => setSourceText(phrase)} className="w-full text-left text-sm px-3 py-2 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-gray-700" data-id="element-742">
                              {phrase}
                            </button>)}
                        </div>
                      </div>)}
                  </div>
                </div> : <div data-id="element-743">
                  <div className="flex items-center justify-between mb-3" data-id="element-744">
                    <h3 className="text-sm font-semibold text-gray-700" data-id="element-745">
                      Recent Translations
                    </h3>
                    <button onClick={() => setShowPhrases(true)} className="text-sm text-blue-600 hover:text-blue-700" data-id="element-746">
                      Show Phrases
                    </button>
                  </div>
                  {translationHistory.length === 0 ? <div className="text-center py-6 text-gray-400 text-sm" data-id="element-747">
                      <HistoryIcon className="h-8 w-8 mx-auto mb-2 opacity-50" data-id="element-748" />
                      <p data-id="element-749">Your translation history will appear here</p>
                    </div> : <div className="flex space-x-3 overflow-x-auto pb-2" data-id="element-750">
                      {translationHistory.slice(0, 10).map(translation => <button key={translation.id} onClick={() => loadFromHistory(translation)} className="flex-shrink-0 w-64 p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border border-blue-200 text-left" data-id="element-751">
                          <div className="flex items-start justify-between mb-2" data-id="element-752">
                            <div className="flex items-center space-x-2 text-xs text-gray-600" data-id="element-753">
                              <span data-id="element-754">
                                {LANGUAGES.find(l => l.code === translation.sourceLang)?.flag}
                              </span>
                              <ArrowLeftRightIcon className="h-3 w-3" data-id="element-755" />
                              <span data-id="element-756">
                                {LANGUAGES.find(l => l.code === translation.targetLang)?.flag}
                              </span>
                            </div>
                            <button onClick={e => {
                      e.stopPropagation();
                      toggleFavorite(translation.id);
                    }} className="text-gray-400 hover:text-yellow-500 transition-colors" data-id="element-757">
                              <StarIcon className={`h-4 w-4 ${translation.isFavorite ? 'fill-yellow-500 text-yellow-500' : ''}`} data-id="element-758" />
                            </button>
                          </div>
                          <p className="text-sm text-gray-700 line-clamp-2 mb-1" data-id="element-759">
                            {translation.sourceText}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-760">
                            {translation.timestamp.toLocaleTimeString()}
                          </p>
                        </button>)}
                    </div>}
                </div>}
            </div>
          </div>
        </div>;
    }
    // Ghiblifi Interface
    if (app.id === 'ghiblifi') {
      return <div className="flex-1 flex overflow-hidden bg-gradient-to-br from-green-50 via-blue-50 to-purple-50" data-id="element-761">
          {/* Left Panel - Upload & Controls */}
          <div className="w-96 border-r border-green-200/50 bg-white/80 backdrop-blur-sm flex flex-col" data-id="element-762">
            <div className="p-6 border-b border-green-200/50" data-id="element-763">
              <div className="flex items-center space-x-2 mb-2" data-id="element-764">
                <CloudIcon className="h-5 w-5 text-green-600" data-id="element-765" />
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-766">
                  Ghibli Transform
                </h2>
              </div>
              <p className="text-sm text-gray-600" data-id="element-767">
                Transform your photos into Studio Ghibli magic
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-768">
              {/* Upload Area */}
              <div data-id="element-769">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-770">
                  Upload Image
                </label>
                {!uploadedImage ? <label className="flex flex-col items-center justify-center h-48 border-2 border-dashed border-green-300 rounded-2xl cursor-pointer hover:border-green-400 hover:bg-green-50/50 transition-all" data-id="element-771">
                    <TreesIcon className="h-12 w-12 text-green-400 mb-3" data-id="element-772" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-773">
                      Drop your image here
                    </span>
                    <span className="text-xs text-gray-500 mt-1" data-id="element-774">
                      or click to browse
                    </span>
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-id="element-775" />
                  </label> : <div className="relative" data-id="element-776">
                    <img src={imagePreview!} alt="Upload" className="w-full h-48 object-cover rounded-2xl" data-id="element-777" />
                    <button onClick={removeImage} className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors shadow-lg" data-id="element-778">
                      <XIcon className="h-4 w-4" data-id="element-779" />
                    </button>
                  </div>}
              </div>

              {/* Style Selection */}
              <div data-id="element-780">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-781">
                  Ghibli Style
                </label>
                <div className="space-y-2" data-id="element-782">
                  {[{
                  id: 'totoro-forest',
                  name: 'Totoro Forest',
                  emoji: '🌳'
                }, {
                  id: 'spirited-away',
                  name: 'Spirited Away',
                  emoji: '🏮'
                }, {
                  id: 'howls-castle',
                  name: "Howl's Castle",
                  emoji: '🏰'
                }, {
                  id: 'ponyo-sea',
                  name: 'Ponyo Sea',
                  emoji: '🌊'
                }, {
                  id: 'classic',
                  name: 'Classic Ghibli',
                  emoji: '✨'
                }].map(style => <button key={style.id} onClick={() => setGhiblifiStyle(style.id as GhiblifiStyle)} className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${ghiblifiStyle === style.id ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-md' : 'bg-green-50 text-gray-700 hover:bg-green-100'}`} data-id="element-783">
                      <span className="text-2xl" data-id="element-784">{style.emoji}</span>
                      <span className="font-medium" data-id="element-785">{style.name}</span>
                    </button>)}
                </div>
              </div>

              {/* Intensity Slider */}
              <div data-id="element-786">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-787">
                  Style Intensity: {styleIntensity}%
                </label>
                <input type="range" min="0" max="100" value={styleIntensity} onChange={e => setStyleIntensity(parseInt(e.target.value))} className="w-full accent-green-500" data-id="element-788" />
                <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-789">
                  <span data-id="element-790">Subtle</span>
                  <span data-id="element-791">Magical</span>
                </div>
              </div>

              {/* Transform Button */}
              <button onClick={handleGhiblifiTransform} disabled={!uploadedImage || isTransforming} className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl font-medium hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2 shadow-lg" data-id="element-792">
                {isTransforming ? <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" data-id="element-793" />
                    <span data-id="element-794">Transforming...</span>
                  </> : <>
                    <SparkleIcon className="h-5 w-5" data-id="element-795" />
                    <span data-id="element-796">Transform to Ghibli</span>
                  </>}
              </button>
            </div>
          </div>

          {/* Right Panel - Results */}
          <div className="flex-1 flex flex-col" data-id="element-797">
            {selectedTransformedImage && showBeforeAfter ? <>
                {/* Before/After Comparison */}
                <div className="flex-1 p-6 flex items-center justify-center" data-id="element-798">
                  <div className="relative max-w-4xl w-full" data-id="element-799">
                    <div className="relative overflow-hidden rounded-2xl shadow-2xl" data-id="element-800">
                      {/* After Image */}
                      <img src={selectedTransformedImage.transformedUrl} alt="Transformed" className="w-full" data-id="element-801" />
                      {/* Before Image with Slider */}
                      <div className="absolute inset-0" style={{
                    clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
                  }} data-id="element-802">
                        <img src={selectedTransformedImage.originalUrl} alt="Original" className="w-full h-full object-cover" data-id="element-803" />
                      </div>
                      {/* Slider */}
                      <div className="absolute inset-y-0 w-1 bg-white shadow-lg cursor-ew-resize" style={{
                    left: `${sliderPosition}%`
                  }} data-id="element-804">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-xl" data-id="element-805">
                          <div className="flex space-x-1" data-id="element-806">
                            <div className="w-1 h-4 bg-gray-400 rounded" data-id="element-807" />
                            <div className="w-1 h-4 bg-gray-400 rounded" data-id="element-808" />
                          </div>
                        </div>
                      </div>
                      <input type="range" min="0" max="100" value={sliderPosition} onChange={e => setSliderPosition(parseInt(e.target.value))} className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize" data-id="element-809" />
                    </div>
                    <div className="flex justify-between mt-4 text-sm font-medium text-gray-600" data-id="element-810">
                      <span data-id="element-811">Before</span>
                      <span data-id="element-812">After</span>
                    </div>
                    <div className="flex space-x-3 mt-4" data-id="element-813">
                      <button onClick={() => handleDownloadImage(selectedTransformedImage as any)} className="flex-1 bg-white border-2 border-green-500 text-green-600 py-2 rounded-xl hover:bg-green-50 transition-colors font-medium" data-id="element-814">
                        <DownloadIcon className="h-4 w-4 inline mr-2" data-id="element-815" />
                        Download
                      </button>
                      <button onClick={() => handleShare(selectedTransformedImage.transformedUrl)} className="flex-1 bg-white border-2 border-green-500 text-green-600 py-2 rounded-xl hover:bg-green-50 transition-colors font-medium" data-id="element-816">
                        <ShareIcon className="h-4 w-4 inline mr-2" data-id="element-817" />
                        Share
                      </button>
                    </div>
                  </div>
                </div>

                {/* Image History */}
                <div className="border-t border-green-200/50 bg-white/80 backdrop-blur-sm p-4" data-id="element-818">
                  <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-819">
                    Recent Transformations
                  </h3>
                  <div className="flex space-x-3 overflow-x-auto pb-2" data-id="element-820">
                    {transformedImages.map(img => <button key={img.id} onClick={() => {
                  setSelectedTransformedImage(img);
                  setShowBeforeAfter(true);
                }} className={`flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden border-2 transition-all ${selectedTransformedImage.id === img.id ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200 hover:border-green-300'}`} data-id="element-821">
                        <img src={img.transformedUrl} alt="" className="w-full h-full object-cover" data-id="element-822" />
                      </button>)}
                  </div>
                </div>
              </> : <div className="flex-1 flex items-center justify-center" data-id="element-823">
                <div className="text-center max-w-md px-4" data-id="element-824">
                  <div className="w-32 h-32 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-825">
                    <CloudIcon className="h-16 w-16 text-green-500" data-id="element-826" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3" data-id="element-827">
                    Ready for Magic
                  </h3>
                  <p className="text-gray-600" data-id="element-828">
                    Upload an image and watch it transform into a Studio Ghibli
                    masterpiece
                  </p>
                </div>
              </div>}
          </div>
        </div>;
    }
    // Cooking Helper Interface
    if (app.id === 'cooking-helper') {
      return <div className="flex-1 flex overflow-hidden bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50" data-id="element-829">
          {/* Left Panel - Recipe Input */}
          <div className="w-96 border-r border-orange-200/50 bg-white/90 backdrop-blur-sm flex flex-col" data-id="element-830">
            <div className="p-6 border-b border-orange-200/50" data-id="element-831">
              <div className="flex items-center space-x-2 mb-2" data-id="element-832">
                <ChefHatIcon className="h-5 w-5 text-orange-600" data-id="element-833" />
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-834">
                  Recipe Generator
                </h2>
              </div>
              <p className="text-sm text-gray-600" data-id="element-835">
                Describe what you want to cook
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-836">
              {/* Recipe Input */}
              <div data-id="element-837">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-838">
                  What would you like to make?
                </label>
                <textarea value={activeTab?.prompt || ''} onChange={e => updateTabPrompt(e.target.value)} placeholder="I want to make pasta carbonara for dinner..." className="w-full border-2 border-orange-200 rounded-xl px-4 py-3 h-32 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" disabled={isGeneratingRecipe} data-id="element-839" />
              </div>

              {/* Quick Preferences */}
              <div data-id="element-840">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-841">
                  Quick Filters
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-842">
                  {['Quick (< 30 min)', 'Vegetarian', 'Low Carb', 'Easy'].map(filter => <button key={filter} className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg text-sm hover:bg-orange-200 transition-colors" data-id="element-843">
                        {filter}
                      </button>)}
                </div>
              </div>

              {/* Generate Button */}
              <button onClick={handleGenerateRecipe} disabled={!activeTab?.prompt.trim() || isGeneratingRecipe} className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 rounded-xl font-medium hover:from-orange-600 hover:to-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2 shadow-lg" data-id="element-844">
                {isGeneratingRecipe ? <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" data-id="element-845" />
                    <span data-id="element-846">Cooking up recipe...</span>
                  </> : <>
                    <FlameIcon className="h-5 w-5" data-id="element-847" />
                    <span data-id="element-848">Generate Recipe</span>
                  </>}
              </button>

              {/* Preferences Button */}
              <button onClick={() => setShowCookingPreferences(true)} className="w-full border-2 border-orange-300 text-orange-700 py-2 rounded-xl hover:bg-orange-50 transition-colors font-medium flex items-center justify-center space-x-2" data-id="element-849">
                <SettingsIcon className="h-4 w-4" data-id="element-850" />
                <span data-id="element-851">Dietary Preferences</span>
              </button>
            </div>
          </div>

          {/* Right Panel - Recipe Display */}
          <div className="flex-1 flex flex-col" data-id="element-852">
            {currentRecipe ? <>
                {/* Recipe Header */}
                <div className="border-b border-orange-200/50 bg-white/90 backdrop-blur-sm p-6" data-id="element-853">
                  <div className="flex items-start justify-between mb-4" data-id="element-854">
                    <div data-id="element-855">
                      <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-856">
                        {currentRecipe.title}
                      </h2>
                      <div className="flex items-center space-x-4 text-sm text-gray-600" data-id="element-857">
                        <div className="flex items-center space-x-1" data-id="element-858">
                          <ClockIcon className="h-4 w-4" data-id="element-859" />
                          <span data-id="element-860">
                            {currentRecipe.prepTime + currentRecipe.cookTime}{' '}
                            min
                          </span>
                        </div>
                        <div className="flex items-center space-x-1" data-id="element-861">
                          <UtensilsIcon className="h-4 w-4" data-id="element-862" />
                          <span data-id="element-863">{currentRecipe.difficulty}</span>
                        </div>
                        <div className="flex items-center space-x-1" data-id="element-864">
                          <ScaleIcon className="h-4 w-4" data-id="element-865" />
                          <span data-id="element-866">
                            {Math.round(currentRecipe.servings * servingMultiplier)}{' '}
                            servings
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2" data-id="element-867">
                      <button onClick={() => setShowNutrition(!showNutrition)} className="p-2 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition-colors" title="Nutrition Info" data-id="element-868">
                        <LeafIcon className="h-5 w-5" data-id="element-869" />
                      </button>
                      <button onClick={addToShoppingList} className="p-2 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition-colors" title="Add to Shopping List" data-id="element-870">
                        <ShoppingCartIcon className="h-5 w-5" data-id="element-871" />
                      </button>
                      <button className="p-2 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition-colors" title="Save Recipe" data-id="element-872">
                        <BookmarkIcon className="h-5 w-5" data-id="element-873" />
                      </button>
                    </div>
                  </div>

                  {/* Serving Adjuster */}
                  <div className="flex items-center space-x-3 bg-orange-50 rounded-xl p-3" data-id="element-874">
                    <span className="text-sm font-medium text-gray-700" data-id="element-875">
                      Servings:
                    </span>
                    <button onClick={() => adjustServings(Math.max(1, Math.round(currentRecipe.servings * servingMultiplier) - 1))} className="w-8 h-8 bg-white border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors" data-id="element-876">
                      -
                    </button>
                    <span className="font-semibold text-gray-900 min-w-[2rem] text-center" data-id="element-877">
                      {Math.round(currentRecipe.servings * servingMultiplier)}
                    </span>
                    <button onClick={() => adjustServings(Math.round(currentRecipe.servings * servingMultiplier) + 1)} className="w-8 h-8 bg-white border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors" data-id="element-878">
                      +
                    </button>
                  </div>
                </div>

                {/* Recipe Content */}
                <div className="flex-1 overflow-y-auto p-6" data-id="element-879">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-880">
                    {/* Ingredients */}
                    <div className="lg:col-span-1" data-id="element-881">
                      <div className="bg-white rounded-2xl shadow-sm border border-orange-200/50 p-6 sticky top-0" data-id="element-882">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-883">
                          <ShoppingCartIcon className="h-5 w-5 mr-2 text-orange-600" data-id="element-884" />
                          Ingredients
                        </h3>
                        <ul className="space-y-3" data-id="element-885">
                          {currentRecipe.ingredients.map((ing, idx) => <li key={idx} className="flex items-start space-x-2" data-id="element-886">
                              <input type="checkbox" className="mt-1 rounded text-orange-600" data-id="element-887" />
                              <span className="text-gray-700" data-id="element-888">
                                <span className="font-medium" data-id="element-889">
                                  {parseFloat(ing.amount) * servingMultiplier}{' '}
                                  {ing.unit}
                                </span>{' '}
                                {ing.item}
                              </span>
                            </li>)}
                        </ul>

                        {/* Nutrition Info */}
                        {showNutrition && currentRecipe.nutrition && <div className="mt-6 pt-6 border-t border-orange-200" data-id="element-890">
                            <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-891">
                              Nutrition (per serving)
                            </h4>
                            <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-892">
                              <div className="bg-orange-50 rounded-lg p-2" data-id="element-893">
                                <div className="text-gray-600" data-id="element-894">Calories</div>
                                <div className="font-semibold text-gray-900" data-id="element-895">
                                  {currentRecipe.nutrition.calories}
                                </div>
                              </div>
                              <div className="bg-orange-50 rounded-lg p-2" data-id="element-896">
                                <div className="text-gray-600" data-id="element-897">Protein</div>
                                <div className="font-semibold text-gray-900" data-id="element-898">
                                  {currentRecipe.nutrition.protein}g
                                </div>
                              </div>
                              <div className="bg-orange-50 rounded-lg p-2" data-id="element-899">
                                <div className="text-gray-600" data-id="element-900">Carbs</div>
                                <div className="font-semibold text-gray-900" data-id="element-901">
                                  {currentRecipe.nutrition.carbs}g
                                </div>
                              </div>
                              <div className="bg-orange-50 rounded-lg p-2" data-id="element-902">
                                <div className="text-gray-600" data-id="element-903">Fat</div>
                                <div className="font-semibold text-gray-900" data-id="element-904">
                                  {currentRecipe.nutrition.fat}g
                                </div>
                              </div>
                            </div>
                          </div>}
                      </div>
                    </div>

                    {/* Steps */}
                    <div className="lg:col-span-2" data-id="element-905">
                      <div className="bg-white rounded-2xl shadow-sm border border-orange-200/50 p-6" data-id="element-906">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-907">
                          <FlameIcon className="h-5 w-5 mr-2 text-orange-600" data-id="element-908" />
                          Instructions
                        </h3>
                        <div className="space-y-4" data-id="element-909">
                          {currentRecipe.steps.map((step, idx) => <div key={idx} className={`flex space-x-4 p-4 rounded-xl transition-all ${completedSteps.includes(idx) ? 'bg-green-50 border-2 border-green-300' : activeStep === idx ? 'bg-orange-50 border-2 border-orange-300' : 'bg-gray-50 border-2 border-gray-200'}`} data-id="element-910">
                              <button onClick={() => toggleStep(idx)} className={`flex-shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center font-semibold transition-all ${completedSteps.includes(idx) ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 text-gray-600 hover:border-orange-500'}`} data-id="element-911">
                                {completedSteps.includes(idx) ? <CheckIcon className="h-4 w-4" data-id="element-912" /> : idx + 1}
                              </button>
                              <div className="flex-1" data-id="element-913">
                                <p className={`text-gray-700 ${completedSteps.includes(idx) ? 'line-through opacity-60' : ''}`} data-id="element-914">
                                  {step}
                                </p>
                              </div>
                            </div>)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </> : <div className="flex-1 flex items-center justify-center" data-id="element-915">
                <div className="text-center max-w-md px-4" data-id="element-916">
                  <div className="w-32 h-32 bg-gradient-to-br from-orange-100 to-red-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-917">
                    <ChefHatIcon className="h-16 w-16 text-orange-500" data-id="element-918" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3" data-id="element-919">
                    Let's Cook Something Delicious
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-920">
                    Tell me what you'd like to make and I'll create a
                    personalized recipe for you
                  </p>
                  <button onClick={() => setShowCookingPreferences(true)} className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all shadow-md" data-id="element-921">
                    <SettingsIcon className="h-5 w-5 mr-2" data-id="element-922" />
                    Set Your Preferences
                  </button>
                </div>
              </div>}
          </div>
        </div>;
    }
    // Image Wizard Pro Interface
    if (app.id === 'image-wizard-pro') {
      return <div className="flex-1 flex overflow-hidden" data-id="element-923">
          {/* Left Panel - Controls */}
          <div className="w-96 border-r border-gray-200 bg-white flex flex-col" data-id="element-924">
            <div className="p-6 border-b border-gray-200" data-id="element-925">
              <h2 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-926">
                Image Generation
              </h2>
              <p className="text-sm text-gray-500" data-id="element-927">
                Create stunning images with AI
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-928">
              {/* Prompt Input */}
              <div data-id="element-929">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-930">
                  Describe your image
                </label>
                <textarea value={activeTab?.prompt || ''} onChange={e => updateTabPrompt(e.target.value)} placeholder="A serene mountain landscape at sunset..." className="w-full border border-gray-300 rounded-lg px-4 py-3 h-32 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" disabled={isGeneratingImage} data-id="element-931" />
              </div>

              {/* Style Selection */}
              <div data-id="element-932">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-933">
                  Style
                </label>
                <div className="grid grid-cols-2 gap-2" data-id="element-934">
                  {['realistic', 'artistic', 'anime', 'digital-art'].map(style => <button key={style} onClick={() => setImageParams({
                  ...imageParams,
                  style
                })} className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${imageParams.style === style ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-935">
                        {style.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </button>)}
                </div>
              </div>

              {/* Aspect Ratio */}
              <div data-id="element-936">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-937">
                  Aspect Ratio
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-938">
                  {['1:1', '16:9', '9:16'].map(ratio => <button key={ratio} onClick={() => setImageParams({
                  ...imageParams,
                  aspectRatio: ratio
                })} className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${imageParams.aspectRatio === ratio ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-939">
                      {ratio}
                    </button>)}
                </div>
              </div>

              {/* Quality */}
              <div data-id="element-940">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-941">
                  Quality
                </label>
                <select value={imageParams.quality} onChange={e => setImageParams({
                ...imageParams,
                quality: e.target.value
              })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-942">
                  <option value="standard" data-id="element-943">Standard</option>
                  <option value="hd" data-id="element-944">HD</option>
                  <option value="ultra" data-id="element-945">Ultra HD</option>
                </select>
              </div>

              {/* Number of Images */}
              <div data-id="element-946">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-947">
                  Number of Images: {imageParams.numImages}
                </label>
                <input type="range" min="1" max="4" value={imageParams.numImages} onChange={e => setImageParams({
                ...imageParams,
                numImages: parseInt(e.target.value)
              })} className="w-full accent-purple-600" data-id="element-948" />
              </div>

              {/* Generate Button */}
              <button onClick={handleGenerateImage} disabled={!activeTab?.prompt.trim() || isGeneratingImage} className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2" data-id="element-949">
                {isGeneratingImage ? <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" data-id="element-950" />
                    <span data-id="element-951">Generating...</span>
                  </> : <>
                    <Sparkles className="h-5 w-5" data-id="element-952" />
                    <span data-id="element-953">Generate Image</span>
                  </>}
              </button>
            </div>
          </div>

          {/* Right Panel - Generated Images */}
          <div className="flex-1 flex flex-col bg-gray-50" data-id="element-954">
            {selectedGeneratedImage ? <>
                {/* Main Image Display */}
                <div className="flex-1 p-6 flex items-center justify-center" data-id="element-955">
                  <div className="relative max-w-4xl w-full" data-id="element-956">
                    <img src={selectedGeneratedImage.url} alt="Generated" className="w-full rounded-xl shadow-2xl" data-id="element-957" />
                    <div className="absolute top-4 right-4 flex space-x-2" data-id="element-958">
                      <button onClick={() => handleDownloadImage(selectedGeneratedImage)} className="bg-white/90 backdrop-blur-sm p-3 rounded-lg hover:bg-white transition-colors shadow-lg" title="Download" data-id="element-959">
                        <DownloadIcon className="h-5 w-5 text-gray-700" data-id="element-960" />
                      </button>
                      <button onClick={() => handleShare(selectedGeneratedImage.prompt)} className="bg-white/90 backdrop-blur-sm p-3 rounded-lg hover:bg-white transition-colors shadow-lg" title="Share" data-id="element-961">
                        <ShareIcon className="h-5 w-5 text-gray-700" data-id="element-962" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Image History */}
                <div className="border-t border-gray-200 bg-white p-4" data-id="element-963">
                  <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-964">
                    Recent Generations
                  </h3>
                  <div className="flex space-x-3 overflow-x-auto pb-2" data-id="element-965">
                    {generatedImages.map(img => <button key={img.id} onClick={() => setSelectedGeneratedImage(img)} className={`flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden border-2 transition-all ${selectedGeneratedImage.id === img.id ? 'border-purple-600 ring-2 ring-purple-200' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-966">
                        <img src={img.url} alt="" className="w-full h-full object-cover" data-id="element-967" />
                      </button>)}
                  </div>
                </div>
              </> : <div className="flex-1 flex items-center justify-center" data-id="element-968">
                <div className="text-center max-w-md px-4" data-id="element-969">
                  <div className="w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-3xl flex items-center justify-center mx-auto mb-6" data-id="element-970">
                    <ImageIcon className="h-12 w-12 text-purple-600" data-id="element-971" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-972">
                    Ready to Create
                  </h3>
                  <p className="text-gray-600" data-id="element-973">
                    Describe your vision and watch AI bring it to life
                  </p>
                </div>
              </div>}
          </div>
        </div>;
    }
    // CodeCraft AI Interface
    if (app.id === 'codecraft-ai') {
      return <div className="flex-1 flex flex-col overflow-hidden" data-id="element-974">
          {/* Top Controls */}
          <div className="border-b border-gray-200 bg-white px-6 py-4" data-id="element-975">
            <div className="flex items-center justify-between" data-id="element-976">
              <div className="flex items-center space-x-4" data-id="element-977">
                <select value={selectedLanguage} onChange={e => setSelectedLanguage(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" data-id="element-978">
                  <option value="javascript" data-id="element-979">JavaScript</option>
                  <option value="typescript" data-id="element-980">TypeScript</option>
                  <option value="python" data-id="element-981">Python</option>
                  <option value="java" data-id="element-982">Java</option>
                  <option value="cpp" data-id="element-983">C++</option>
                  <option value="rust" data-id="element-984">Rust</option>
                </select>

                <div className="flex space-x-2" data-id="element-985">
                  {(['optimize', 'explain', 'debug', 'convert'] as CodeAction[]).map(action => <button key={action} onClick={() => setCodeAction(action)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${codeAction === action ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-986">
                      {action.charAt(0).toUpperCase() + action.slice(1)}
                    </button>)}
                </div>
              </div>

              <button onClick={handleProcessCode} disabled={!inputCode.trim() || isProcessingCode} className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-2 rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center space-x-2" data-id="element-987">
                {isProcessingCode ? <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" data-id="element-988" />
                    <span data-id="element-989">Processing...</span>
                  </> : <>
                    <PlayIcon className="h-4 w-4" data-id="element-990" />
                    <span data-id="element-991">Run</span>
                  </>}
              </button>
            </div>
          </div>

          {/* Code Editors - Split View */}
          <div className="flex-1 flex overflow-hidden" data-id="element-992">
            {/* Input Code */}
            <div className="flex-1 flex flex-col border-r border-gray-200" data-id="element-993">
              <div className="bg-gray-800 text-gray-100 px-4 py-2 flex items-center justify-between" data-id="element-994">
                <span className="text-sm font-medium" data-id="element-995">Input Code</span>
                <button onClick={() => setInputCode('')} className="text-gray-400 hover:text-white transition-colors" title="Clear" data-id="element-996">
                  <Trash2Icon className="h-4 w-4" data-id="element-997" />
                </button>
              </div>
              <textarea value={inputCode} onChange={e => setInputCode(e.target.value)} placeholder="// Paste your code here..." className="flex-1 bg-gray-900 text-gray-100 p-4 font-mono text-sm resize-none focus:outline-none" spellCheck={false} data-id="element-998" />
            </div>

            {/* Output Code */}
            <div className="flex-1 flex flex-col" data-id="element-999">
              <div className="bg-gray-800 text-gray-100 px-4 py-2 flex items-center justify-between" data-id="element-1000">
                <span className="text-sm font-medium" data-id="element-1001">Output Code</span>
                <div className="flex space-x-2" data-id="element-1002">
                  <button onClick={() => handleCopyCode(outputCode)} disabled={!outputCode} className="text-gray-400 hover:text-white transition-colors disabled:opacity-50" title="Copy" data-id="element-1003">
                    <CopyIcon className="h-4 w-4" data-id="element-1004" />
                  </button>
                  <button onClick={() => {
                  const blob = new Blob([outputCode], {
                    type: 'text/plain'
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `code.${selectedLanguage}`;
                  a.click();
                }} disabled={!outputCode} className="text-gray-400 hover:text-white transition-colors disabled:opacity-50" title="Download" data-id="element-1005">
                    <DownloadIcon className="h-4 w-4" data-id="element-1006" />
                  </button>
                </div>
              </div>
              <div className="flex-1 bg-gray-900 text-gray-100 p-4 font-mono text-sm overflow-auto" data-id="element-1007">
                {outputCode || <div className="text-gray-500 italic" data-id="element-1008">
                    Processed code will appear here...
                  </div>}
              </div>
            </div>
          </div>

          {/* Console Output */}
          <div className="border-t border-gray-200 bg-gray-900 h-32 overflow-y-auto" data-id="element-1009">
            <div className="bg-gray-800 text-gray-100 px-4 py-2 flex items-center justify-between" data-id="element-1010">
              <span className="text-sm font-medium" data-id="element-1011">Console</span>
              <button onClick={() => setConsoleOutput([])} className="text-gray-400 hover:text-white transition-colors text-xs" data-id="element-1012">
                Clear
              </button>
            </div>
            <div className="p-4 space-y-1 font-mono text-sm" data-id="element-1013">
              {consoleOutput.length === 0 ? <div className="text-gray-500" data-id="element-1014">Ready to process code...</div> : consoleOutput.map((line, i) => <div key={i} className="text-gray-300" data-id="element-1015">
                    {line}
                  </div>)}
            </div>
          </div>
        </div>;
    }
    // Default interface for other apps
    return <div className="flex-1 flex flex-col bg-white m-3 md:m-4 rounded-xl shadow-lg border border-gray-200 overflow-hidden" data-id="element-1016">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4" data-id="element-1017">
          {!activeTab?.messages.length ? <div className="h-full flex items-center justify-center" data-id="element-1018">
              <div className="text-center max-w-md px-4" data-id="element-1019">
                <div className={`${app.iconBg} w-20 h-20 md:w-24 md:h-24 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl`} data-id="element-1020">
                  <div className="scale-150" data-id="element-1021">{getIcon(app.icon)}</div>
                </div>
                <h2 className="text-xl md:text-2xl font-bold mb-3 text-gray-900" data-id="element-1022">
                  Welcome to {app.title}
                </h2>
                <p className="text-sm md:text-base text-gray-600 mb-8" data-id="element-1023">
                  {app.description}
                </p>
                {app.id === 'cooking-helper' && <button onClick={() => setShowCookingPreferences(true)} className="mb-6 inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all shadow-md" data-id="element-1024">
                    <ChefHatIcon className="h-5 w-5 mr-2" data-id="element-1025" />
                    Set Your Cooking Preferences
                  </button>}
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl p-5 md:p-6 text-left" data-id="element-1026">
                  <p className="text-sm font-semibold text-indigo-900 mb-4 flex items-center" data-id="element-1027">
                    <span className="text-2xl mr-2" data-id="element-1028">
                      {welcomeContent.emoji}
                    </span>
                    Try asking:
                  </p>
                  <ul className="text-sm text-indigo-700 space-y-3" data-id="element-1029">
                    {welcomeContent.examples.map((example, index) => <li key={index} className="flex items-start" data-id="element-1030">
                        <span className="mr-2 mt-0.5" data-id="element-1031">•</span>
                        <span data-id="element-1032">{example}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </div> : <>
              {activeTab.messages.map(message => <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-1033">
                  <div className={`max-w-[85%] md:max-w-[75%] rounded-2xl p-4 md:p-5 shadow-md ${message.type === 'user' ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white' : 'bg-gray-50 border border-gray-200'}`} data-id="element-1034">
                    <div className="flex items-center justify-between mb-2" data-id="element-1035">
                      <div className="flex items-center space-x-2" data-id="element-1036">
                        {message.type === 'assistant' && <div className={`w-6 h-6 ${app.iconBg} rounded-lg flex items-center justify-center`} data-id="element-1037">
                            <div className="scale-75" data-id="element-1038">{getIcon(app.icon)}</div>
                          </div>}
                        <span className="text-xs font-semibold opacity-80" data-id="element-1039">
                          {message.type === 'user' ? 'You' : app.title}
                        </span>
                      </div>
                      <span className="text-xs opacity-60" data-id="element-1040">
                        {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                      </span>
                    </div>
                    <p className="text-sm md:text-base whitespace-pre-wrap leading-relaxed" data-id="element-1041">
                      {message.content}
                    </p>
                    {message.type === 'assistant' && <div className="flex items-center space-x-1 mt-4 pt-3 border-t border-gray-200" data-id="element-1042">
                        <button onClick={() => handleCopy(message.content)} className="p-2 hover:bg-gray-200 rounded-lg transition-colors" title="Copy" data-id="element-1043">
                          <CopyIcon className="h-4 w-4 text-gray-600" data-id="element-1044" />
                        </button>
                        <button onClick={() => handleShare(message.content)} className="p-2 hover:bg-gray-200 rounded-lg transition-colors" title="Share" data-id="element-1045">
                          <ShareIcon className="h-4 w-4 text-gray-600" data-id="element-1046" />
                        </button>
                        <button onClick={() => handleDownload(message.content)} className="p-2 hover:bg-gray-200 rounded-lg transition-colors" title="Download" data-id="element-1047">
                          <DownloadIcon className="h-4 w-4 text-gray-600" data-id="element-1048" />
                        </button>
                        <button className="p-2 hover:bg-gray-200 rounded-lg transition-colors" title="Good response" data-id="element-1049">
                          <ThumbsUpIcon className="h-4 w-4 text-gray-600" data-id="element-1050" />
                        </button>
                        <button className="p-2 hover:bg-gray-200 rounded-lg transition-colors" title="Bad response" data-id="element-1051">
                          <ThumbsDownIcon className="h-4 w-4 text-gray-600" data-id="element-1052" />
                        </button>
                        <button className="p-2 hover:bg-gray-200 rounded-lg transition-colors" title="Regenerate" data-id="element-1053">
                          <RotateCcwIcon className="h-4 w-4 text-gray-600" data-id="element-1054" />
                        </button>
                      </div>}
                  </div>
                </div>)}
              {isGenerating && <div className="flex justify-start" data-id="element-1055">
                  <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 max-w-[85%] md:max-w-[75%] shadow-md" data-id="element-1056">
                    <div className="flex items-center space-x-3" data-id="element-1057">
                      <div className="flex space-x-1.5" data-id="element-1058">
                        <div className="w-2.5 h-2.5 bg-indigo-600 rounded-full animate-bounce" data-id="element-1059" />
                        <div className="w-2.5 h-2.5 bg-indigo-600 rounded-full animate-bounce [animation-delay:0.2s]" data-id="element-1060" />
                        <div className="w-2.5 h-2.5 bg-indigo-600 rounded-full animate-bounce [animation-delay:0.4s]" data-id="element-1061" />
                      </div>
                      <span className="text-sm text-gray-600 font-medium" data-id="element-1062">
                        Generating response...
                      </span>
                    </div>
                  </div>
                </div>}
            </>}
        </div>
        {/* Input Area */}
        <div className="border-t border-gray-200 bg-gradient-to-b from-gray-50 to-white p-4 md:p-5" data-id="element-1063">
          <form onSubmit={handleSubmit} className="max-w-4xl mx-auto" data-id="element-1064">
            {/* Image Preview */}
            {imagePreview && <div className="mb-3 relative inline-block" data-id="element-1065">
                <img src={imagePreview} alt="Upload preview" className="h-20 w-20 object-cover rounded-lg border-2 border-indigo-500" data-id="element-1066" />
                <button type="button" onClick={removeImage} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition-colors" data-id="element-1067">
                  <XIcon className="h-3 w-3" data-id="element-1068" />
                </button>
              </div>}
            <div className="flex items-start gap-3" data-id="element-1069">
              <div className="flex-1 relative" data-id="element-1070">
                <textarea value={activeTab?.prompt || ''} onChange={e => updateTabPrompt(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }} placeholder="Type your message..." className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none text-sm md:text-base shadow-sm" rows={2} disabled={isGenerating} data-id="element-1071" />
                {activeTab && activeTab.messages.length > 0 && <button type="button" onClick={handleClearHistory} className="absolute right-3 top-3 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Clear conversation" data-id="element-1072">
                    <Trash2Icon className="h-4 w-4" data-id="element-1073" />
                  </button>}
              </div>
              {/* Upload Button - Show for apps that allow image upload */}
              {(app.id === 'ghiblifi' || app.id === 'image-wizard-pro' || app.id === 'art-generator') && <label className="cursor-pointer bg-gray-100 text-gray-700 p-3 rounded-xl hover:bg-gray-200 transition-all shadow-sm flex-shrink-0" data-id="element-1074">
                  <UploadIcon className="h-5 w-5" data-id="element-1075" />
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-id="element-1076" />
                </label>}
              <button type="submit" disabled={!activeTab?.prompt.trim() || isGenerating} className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-3 rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg flex-shrink-0" aria-label="Send message" data-id="element-1077">
                <SendIcon className="h-5 w-5" data-id="element-1078" />
              </button>
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-gray-500" data-id="element-1079">
              <span className="hidden md:inline" data-id="element-1080">
                Press Enter to send, Shift + Enter for new line
              </span>
              <span className="md:hidden" data-id="element-1081">Tap send or press Enter</span>
              <span className="font-medium" data-id="element-1082">
                {activeTab?.prompt.length || 0} characters
              </span>
            </div>
          </form>
        </div>
      </div>;
  };
  return <div className="h-screen flex flex-col bg-gray-50" data-id="element-1083">
      {/* Unified Header with Tabs */}
      <div className="bg-white border-b border-gray-200 shadow-sm" data-id="element-1084">
        <div className="flex items-center h-16 px-4" data-id="element-1085">
          {/* Left: Back Button */}
          <button onClick={goBackToMyApps} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600 hover:text-indigo-600 mr-3" aria-label="Back to My Apps" title="Back to My Apps" data-id="element-1086">
            <ChevronLeftIcon className="h-5 w-5" data-id="element-1087" />
          </button>
          {/* Center: Tabs - Desktop */}
          <div className="hidden md:flex flex-1 items-center space-x-2 overflow-x-auto px-2" data-id="element-1088">
            {tabs.map(tab => {
            const tabApp = mockApps.find(a => a.id === tab.appId);
            if (!tabApp) return null;
            return <button key={tab.id} onClick={() => {
              setActiveTabId(tab.id);
              navigate(`/app-interface/${tab.appId}`);
            }} className={`group flex items-center space-x-2.5 px-4 py-2.5 rounded-lg transition-all min-w-[180px] ${activeTabId === tab.id ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`} data-id="element-1089">
                  <div className={`w-8 h-8 ${tabApp.iconBg} rounded-lg flex items-center justify-center flex-shrink-0 ${activeTabId === tab.id ? 'bg-white/20' : ''}`} data-id="element-1090">
                    {getIcon(tabApp.icon)}
                  </div>
                  <span className="font-medium truncate flex-1 text-left" data-id="element-1091">
                    {tabApp.title}
                  </span>
                  <button onClick={e => {
                e.stopPropagation();
                closeTab(tab.id);
              }} className={`p-1 rounded transition-colors ${activeTabId === tab.id ? 'hover:bg-white/20' : 'hover:bg-gray-200'}`} aria-label="Close tab" data-id="element-1092">
                    <XIcon className="h-4 w-4" data-id="element-1093" />
                  </button>
                </button>;
          })}
            <button onClick={() => setShowAppPicker(true)} className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-600 flex-shrink-0" aria-label="Open new app" title="Open new app" data-id="element-1094">
              <PlusIcon className="h-5 w-5" data-id="element-1095" />
            </button>
          </div>
          {/* Center: Mobile Tab Info */}
          <div className="md:hidden flex-1 flex items-center justify-center" data-id="element-1096">
            <div className="flex items-center space-x-2" data-id="element-1097">
              <div className={`w-8 h-8 ${app.iconBg} rounded-lg flex items-center justify-center`} data-id="element-1098">
                {getIcon(app.icon)}
              </div>
              <div className="text-left" data-id="element-1099">
                <h1 className="font-semibold text-sm text-gray-900 truncate max-w-[150px]" data-id="element-1100">
                  {app.title}
                </h1>
                <p className="text-xs text-gray-500" data-id="element-1101">
                  Tab {tabs.findIndex(t => t.id === activeTabId) + 1} of{' '}
                  {tabs.length}
                </p>
              </div>
            </div>
          </div>
          {/* Right: Controls */}
          <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0" data-id="element-1102">
            {/* Cooking Preferences Button - Only for Cooking Helper */}
            {app.id === 'cooking-helper' && <button onClick={() => setShowCookingPreferences(true)} className={`p-2 rounded-lg hover:bg-gray-100 transition-colors ${showCookingPreferences ? 'bg-orange-100 text-orange-600' : 'text-gray-600'}`} aria-label="Cooking Preferences" title="Cooking Preferences" data-id="element-1103">
                <ChefHatIcon className="h-5 w-5" data-id="element-1104" />
              </button>}
            {/* Mobile: Tab Menu */}
            <div className="md:hidden flex items-center space-x-1" data-id="element-1105">
              <button onClick={() => setShowAppPicker(true)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Open new app" data-id="element-1106">
                <PlusIcon className="h-5 w-5 text-gray-600" data-id="element-1107" />
              </button>
              {tabs.length > 1 && <button onClick={() => setShowTabMenu(!showTabMenu)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Switch tabs" data-id="element-1108">
                  <MoreHorizontalIcon className="h-5 w-5 text-gray-600" data-id="element-1109" />
                </button>}
            </div>
            <button onClick={() => setShowHistory(!showHistory)} className={`p-2 rounded-lg hover:bg-gray-100 transition-colors ${showHistory ? 'bg-gray-100 text-indigo-600' : 'text-gray-600'}`} aria-label="History" title="History" data-id="element-1110">
              <HistoryIcon className="h-5 w-5" data-id="element-1111" />
            </button>
            <button onClick={() => setShowSettings(!showSettings)} className={`p-2 rounded-lg hover:bg-gray-100 transition-colors ${showSettings ? 'bg-gray-100 text-indigo-600' : 'text-gray-600'}`} aria-label="Settings" title="Settings" data-id="element-1112">
              <SettingsIcon className="h-5 w-5" data-id="element-1113" />
            </button>
          </div>
        </div>
        {/* Mobile Tab Menu */}
        {showTabMenu && <div className="md:hidden absolute top-16 left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-50 max-h-64 overflow-y-auto" data-id="element-1114">
            {tabs.map(tab => {
          const tabApp = mockApps.find(a => a.id === tab.appId);
          if (!tabApp) return null;
          return <button key={tab.id} onClick={() => {
            setActiveTabId(tab.id);
            navigate(`/app-interface/${tab.appId}`);
            setShowTabMenu(false);
          }} className={`w-full flex items-center space-x-3 px-4 py-3 border-b border-gray-100 ${activeTabId === tab.id ? 'bg-indigo-50' : 'hover:bg-gray-50'}`} data-id="element-1115">
                  <div className={`w-10 h-10 ${tabApp.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`} data-id="element-1116">
                    {getIcon(tabApp.icon)}
                  </div>
                  <span className="font-medium flex-1 text-left" data-id="element-1117">
                    {tabApp.title}
                  </span>
                  <button onClick={e => {
              e.stopPropagation();
              closeTab(tab.id);
              if (tabs.length === 1) setShowTabMenu(false);
            }} className="p-1.5 hover:bg-gray-200 rounded" aria-label="Close tab" data-id="element-1118">
                    <XIcon className="h-4 w-4 text-gray-500" data-id="element-1119" />
                  </button>
                </button>;
        })}
          </div>}
      </div>
      <div className="flex-1 flex overflow-hidden" data-id="element-1120">
        {renderAppInterface()}
        {/* Settings Panel */}
        {showSettings && <div className="w-full md:w-80 absolute md:relative top-0 bottom-0 right-0 md:border-l border-gray-200 bg-white overflow-y-auto z-40 shadow-xl md:shadow-none" data-id="element-1121">
            <div className="p-6" data-id="element-1122">
              <div className="flex items-center justify-between mb-6" data-id="element-1123">
                <h3 className="font-bold text-lg" data-id="element-1124">Settings</h3>
                <button onClick={() => setShowSettings(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Close settings" data-id="element-1125">
                  <XIcon className="h-5 w-5 text-gray-600" data-id="element-1126" />
                </button>
              </div>
              <div className="space-y-6" data-id="element-1127">
                <div data-id="element-1128">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1129">
                    AI Model
                  </label>
                  <select value={settings.model} onChange={e => setSettings({
                ...settings,
                model: e.target.value
              })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1130">
                    <option value="gpt-4" data-id="element-1131">GPT-4</option>
                    <option value="gpt-3.5" data-id="element-1132">GPT-3.5 Turbo</option>
                    <option value="claude" data-id="element-1133">Claude 2</option>
                    <option value="gemini" data-id="element-1134">Gemini Pro</option>
                  </select>
                </div>
                <div data-id="element-1135">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1136">
                    Temperature: {settings.temperature}
                  </label>
                  <input type="range" min="0" max="2" step="0.1" value={settings.temperature} onChange={e => setSettings({
                ...settings,
                temperature: parseFloat(e.target.value)
              })} className="w-full" data-id="element-1137" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-1138">
                    <span data-id="element-1139">Precise</span>
                    <span data-id="element-1140">Creative</span>
                  </div>
                </div>
                <div data-id="element-1141">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1142">
                    Max Tokens: {settings.maxTokens}
                  </label>
                  <input type="range" min="100" max="4000" step="100" value={settings.maxTokens} onChange={e => setSettings({
                ...settings,
                maxTokens: parseInt(e.target.value)
              })} className="w-full" data-id="element-1143" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-1144">
                    <span data-id="element-1145">Short</span>
                    <span data-id="element-1146">Long</span>
                  </div>
                </div>
                <div data-id="element-1147">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1148">
                    Top P: {settings.topP}
                  </label>
                  <input type="range" min="0" max="1" step="0.1" value={settings.topP} onChange={e => setSettings({
                ...settings,
                topP: parseFloat(e.target.value)
              })} className="w-full" data-id="element-1149" />
                </div>
                <div className="pt-4 border-t border-gray-200" data-id="element-1150">
                  <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1151">
                    Quick Actions
                  </h4>
                  <div className="space-y-2" data-id="element-1152">
                    <button className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1153">
                      Export Conversation
                    </button>
                    <button className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1154">
                      View App Details
                    </button>
                    <button className="w-full text-left px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors" data-id="element-1155">
                      Reset Settings
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>}
        {/* History Panel */}
        {showHistory && <div className="w-full md:w-80 absolute md:relative top-0 bottom-0 right-0 md:border-l border-gray-200 bg-white overflow-y-auto z-40 shadow-xl md:shadow-none" data-id="element-1156">
            <div className="p-6" data-id="element-1157">
              <div className="flex items-center justify-between mb-6" data-id="element-1158">
                <h3 className="font-bold text-lg" data-id="element-1159">History</h3>
                <button onClick={() => setShowHistory(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Close history" data-id="element-1160">
                  <XIcon className="h-5 w-5 text-gray-600" data-id="element-1161" />
                </button>
              </div>
              {!activeTab?.history.length ? <div className="text-center py-12" data-id="element-1162">
                  <HistoryIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" data-id="element-1163" />
                  <p className="text-sm text-gray-500" data-id="element-1164">No history yet</p>
                  <p className="text-xs text-gray-400 mt-1" data-id="element-1165">
                    Your prompts will appear here
                  </p>
                </div> : <div className="space-y-2" data-id="element-1166">
                  {activeTab.history.map(item => <button key={item.id} onClick={() => loadHistoryItem(item)} className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200" data-id="element-1167">
                      <p className="text-sm text-gray-900 line-clamp-2 mb-1.5" data-id="element-1168">
                        {item.prompt}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-1169">
                        {item.timestamp.toLocaleString()}
                      </p>
                    </button>)}
                </div>}
            </div>
          </div>}
      </div>
      {/* Cooking Preferences Modal */}
      {showCookingPreferences && app.id === 'cooking-helper' && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1170">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden" data-id="element-1171">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-red-500" data-id="element-1172">
              <div className="flex items-center space-x-3" data-id="element-1173">
                <ChefHatIcon className="h-7 w-7 text-white" data-id="element-1174" />
                <h2 className="text-2xl font-bold text-white" data-id="element-1175">
                  Cooking Preferences
                </h2>
              </div>
              <button onClick={() => setShowCookingPreferences(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors" aria-label="Close" data-id="element-1176">
                <XIcon className="h-6 w-6 text-white" data-id="element-1177" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-88px)]" data-id="element-1178">
              <div className="space-y-8" data-id="element-1179">
                {/* Measurement System */}
                <div data-id="element-1180">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-1181">
                    Measurement System
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-1182">
                    <button onClick={() => updateCookingPreferences({
                  measurementSystem: 'metric'
                })} className={`p-4 rounded-xl border-2 transition-all ${activeTab?.cookingPreferences?.measurementSystem === 'metric' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1183">
                      <div className="font-semibold" data-id="element-1184">Metric</div>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-1185">
                        grams, liters, celsius
                      </div>
                    </button>
                    <button onClick={() => updateCookingPreferences({
                  measurementSystem: 'imperial'
                })} className={`p-4 rounded-xl border-2 transition-all ${activeTab?.cookingPreferences?.measurementSystem === 'imperial' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1186">
                      <div className="font-semibold" data-id="element-1187">Imperial</div>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-1188">
                        cups, ounces, fahrenheit
                      </div>
                    </button>
                  </div>
                </div>
                {/* Skill Level */}
                <div data-id="element-1189">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-1190">
                    Cooking Skill Level
                  </label>
                  <div className="grid grid-cols-3 gap-3" data-id="element-1191">
                    {['beginner', 'intermediate', 'advanced'].map(level => <button key={level} onClick={() => updateCookingPreferences({
                  skillLevel: level as any
                })} className={`p-3 rounded-xl border-2 transition-all capitalize ${activeTab?.cookingPreferences?.skillLevel === level ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1192">
                        {level}
                      </button>)}
                  </div>
                </div>
                {/* Serving Size */}
                <div data-id="element-1193">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-1194">
                    Default Serving Size:{' '}
                    {activeTab?.cookingPreferences?.servingSize} people
                  </label>
                  <input type="range" min="1" max="12" value={activeTab?.cookingPreferences?.servingSize || 4} onChange={e => updateCookingPreferences({
                servingSize: parseInt(e.target.value)
              })} className="w-full" data-id="element-1195" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-1196">
                    <span data-id="element-1197">1</span>
                    <span data-id="element-1198">12</span>
                  </div>
                </div>
                {/* Cooking Time */}
                <div data-id="element-1199">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-1200">
                    Preferred Cooking Time
                  </label>
                  <select value={activeTab?.cookingPreferences?.cookingTime || 'any'} onChange={e => updateCookingPreferences({
                cookingTime: e.target.value
              })} className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500" data-id="element-1201">
                    <option value="any" data-id="element-1202">Any time</option>
                    <option value="15min" data-id="element-1203">Under 15 minutes</option>
                    <option value="30min" data-id="element-1204">Under 30 minutes</option>
                    <option value="1hour" data-id="element-1205">Under 1 hour</option>
                    <option value="2hours" data-id="element-1206">1-2 hours</option>
                  </select>
                </div>
                {/* Dietary Restrictions */}
                <div data-id="element-1207">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-1208">
                    Dietary Restrictions
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-1209">
                    {dietaryOptions.map(diet => <button key={diet} onClick={() => toggleArrayItem(activeTab?.cookingPreferences?.dietaryRestrictions || [], diet, 'dietaryRestrictions')} className={`px-3 py-2 rounded-lg text-sm transition-all ${activeTab?.cookingPreferences?.dietaryRestrictions.includes(diet) ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1210">
                        {diet}
                      </button>)}
                  </div>
                </div>
                {/* Allergies */}
                <div data-id="element-1211">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-1212">
                    Allergies
                  </label>
                  <input type="text" placeholder="Enter allergies separated by commas (e.g., peanuts, shellfish)" value={activeTab?.cookingPreferences?.allergies.join(', ') || ''} onChange={e => {
                const allergies = e.target.value.split(',').map(a => a.trim()).filter(a => a);
                updateCookingPreferences({
                  allergies
                });
              }} className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500" data-id="element-1213" />
                </div>
                {/* Available Equipment */}
                <div data-id="element-1214">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-1215">
                    Available Equipment
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-1216">
                    {equipmentOptions.map(equipment => <button key={equipment} onClick={() => toggleArrayItem(activeTab?.cookingPreferences?.availableEquipment || [], equipment, 'availableEquipment')} className={`px-3 py-2 rounded-lg text-sm transition-all ${activeTab?.cookingPreferences?.availableEquipment.includes(equipment) ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1217">
                        {equipment}
                      </button>)}
                  </div>
                </div>
                {/* Cuisine Preferences */}
                <div data-id="element-1218">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-1219">
                    Favorite Cuisines
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-1220">
                    {cuisineOptions.map(cuisine => <button key={cuisine} onClick={() => toggleArrayItem(activeTab?.cookingPreferences?.cuisinePreferences || [], cuisine, 'cuisinePreferences')} className={`px-3 py-2 rounded-lg text-sm transition-all ${activeTab?.cookingPreferences?.cuisinePreferences.includes(cuisine) ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1221">
                        {cuisine}
                      </button>)}
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200" data-id="element-1222">
                <button onClick={() => setShowCookingPreferences(false)} className="px-6 py-3 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium" data-id="element-1223">
                  Cancel
                </button>
                <button onClick={() => setShowCookingPreferences(false)} className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all shadow-md font-medium" data-id="element-1224">
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* App Picker Modal */}
      {showAppPicker && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1225">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden" data-id="element-1226">
            <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-1227">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-1228">Open an App</h2>
              <button onClick={() => setShowAppPicker(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Close" data-id="element-1229">
                <XIcon className="h-6 w-6 text-gray-500" data-id="element-1230" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-88px)]" data-id="element-1231">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1232">
                {mockApps.map(app => <button key={app.id} onClick={() => openAppInNewTab(app.id)} className="flex items-center space-x-4 p-4 border-2 border-gray-200 rounded-xl hover:border-indigo-500 hover:bg-indigo-50 transition-all group" data-id="element-1233">
                    <div className={`${app.iconBg} w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`} data-id="element-1234">
                      {getIcon(app.icon)}
                    </div>
                    <div className="flex-1 text-left min-w-0" data-id="element-1235">
                      <h3 className="font-semibold text-gray-900 truncate" data-id="element-1236">
                        {app.title}
                      </h3>
                      <p className="text-sm text-gray-500 line-clamp-2" data-id="element-1237">
                        {app.description}
                      </p>
                    </div>
                  </button>)}
              </div>
            </div>
          </div>
        </div>}
      {/* Share Modal */}
      {showShareModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1238">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" data-id="element-1239">
            <div className="flex items-center justify-between mb-6" data-id="element-1240">
              <h3 className="text-xl font-bold text-gray-900" data-id="element-1241">
                Share Response
              </h3>
              <button onClick={() => setShowShareModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Close" data-id="element-1242">
                <XIcon className="h-5 w-5 text-gray-500" data-id="element-1243" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-1244">
              <button onClick={() => handleShareTo('twitter')} className="flex items-center justify-center space-x-2 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all" data-id="element-1245">
                <TwitterIcon className="h-5 w-5 text-blue-400" data-id="element-1246" />
                <span className="font-medium" data-id="element-1247">Twitter</span>
              </button>
              <button onClick={() => handleShareTo('facebook')} className="flex items-center justify-center space-x-2 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-600 hover:bg-blue-50 transition-all" data-id="element-1248">
                <FacebookIcon className="h-5 w-5 text-blue-600" data-id="element-1249" />
                <span className="font-medium" data-id="element-1250">Facebook</span>
              </button>
              <button onClick={() => handleShareTo('linkedin')} className="flex items-center justify-center space-x-2 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-700 hover:bg-blue-50 transition-all" data-id="element-1251">
                <LinkedinIcon className="h-5 w-5 text-blue-700" data-id="element-1252" />
                <span className="font-medium" data-id="element-1253">LinkedIn</span>
              </button>
              <button onClick={() => handleShareTo('copy')} className="flex items-center justify-center space-x-2 p-4 border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-all" data-id="element-1254">
                <LinkIcon className="h-5 w-5 text-gray-600" data-id="element-1255" />
                <span className="font-medium" data-id="element-1256">Copy Link</span>
              </button>
            </div>
          </div>
        </div>}
      {/* Overlay for mobile panels */}
      {(showSettings || showHistory) && <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30" onClick={() => {
      setShowSettings(false);
      setShowHistory(false);
    }} data-id="element-1257" />}
    </div>;
};